import * as React from "react";
import { format } from "date-fns";
import { CalendarIcon, XIcon } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Input } from "@/components/ui/input";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

/** `datetime-local`-compatible value: `YYYY-MM-DDTHH:mm` */
export type DateTimeLocalValue = string;

export interface DateTimePickerProps {
  value?: DateTimeLocalValue | null;
  onChange: (value: DateTimeLocalValue) => void;
  id?: string;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
  /** When true, show a clear control that resets to empty string. Default true. */
  clearable?: boolean;
}

const DATETIME_LOCAL_RE =
  /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/;

function parseDateTimeLocal(
  value: string | null | undefined,
): Date | undefined {
  if (!value) return undefined;
  const match = DATETIME_LOCAL_RE.exec(value);
  if (!match) return undefined;
  const [, y, m, d, h, min] = match;
  const date = new Date(
    Number(y),
    Number(m) - 1,
    Number(d),
    Number(h),
    Number(min),
  );
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function toDateTimeLocal(date: Date): DateTimeLocalValue {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function datePart(value: string | null | undefined): string {
  const date = parseDateTimeLocal(value);
  if (!date) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

function timePart(value: string | null | undefined): string {
  const date = parseDateTimeLocal(value);
  if (!date) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function combineDateAndTime(
  date: Date,
  time: string | null | undefined,
): DateTimeLocalValue {
  const [hours = "00", minutes = "00"] = (time || "00:00").split(":");
  const next = new Date(date);
  next.setHours(Number(hours) || 0, Number(minutes) || 0, 0, 0);
  return toDateTimeLocal(next);
}

function DateTimePicker({
  value,
  onChange,
  id,
  disabled = false,
  placeholder = "Pick date and time",
  className,
  clearable = true,
}: DateTimePickerProps) {
  const [open, setOpen] = React.useState(false);
  const selected = parseDateTimeLocal(value);
  const display =
    selected != null ? format(selected, "MMM d, yyyy HH:mm") : null;

  const handleSelectDay = (day: Date | undefined) => {
    if (!day) {
      onChange("");
      return;
    }
    onChange(combineDateAndTime(day, timePart(value) || "00:00"));
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextTime = event.target.value;
    if (!nextTime) {
      if (!datePart(value)) {
        onChange("");
        return;
      }
      onChange(`${datePart(value)}T00:00`);
      return;
    }
    const base = selected ?? new Date();
    onChange(combineDateAndTime(base, nextTime));
  };

  const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value);
  };

  const handleClear = () => {
    onChange("");
  };

  return (
    <div className={cn("flex gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className={cn(
              "min-w-0 flex-1 justify-start text-left font-normal",
              !display && "text-muted-foreground",
            )}
          >
            <CalendarIcon className="size-4 shrink-0" />
            <span className="truncate">{display ?? placeholder}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            mode="single"
            selected={selected}
            onSelect={handleSelectDay}
            {...(selected ? { defaultMonth: selected } : {})}
          />
          <div className="flex items-center gap-2 border-t p-3">
            <Input
              type="time"
              value={timePart(value)}
              onChange={handleTimeChange}
              disabled={disabled}
              aria-label="Time"
              className="w-auto"
            />
            {clearable && value ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                disabled={disabled}
                onClick={handleClear}
              >
                Clear
              </Button>
            ) : null}
          </div>
        </PopoverContent>
      </Popover>
      <Input
        type="datetime-local"
        value={value ?? ""}
        onChange={handleTextChange}
        disabled={disabled}
        aria-label={placeholder}
        className="w-46 shrink-0"
      />
      {clearable && value ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          disabled={disabled}
          onClick={handleClear}
          aria-label="Clear date and time"
          className="shrink-0"
        >
          <XIcon className="size-4" />
        </Button>
      ) : null}
    </div>
  );
}

export { DateTimePicker };
